import { CONVERSATION_ENDED } from '@rsos/constants/chatStatuses';
import {
  getConversationID,
  getHasConversationBeenInitiated,
  getIsConversationEnded,
  useGetConversation,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';

const LIVESTREAM_PROMPT = 'Tap link to live-stream video to first responder.';

interface RoomLink {
  conversationId: string;
  roomId?: string;
  refreshToken?: string;
}

/**
 * Texts the caller their link into a video room over one of their chats.
 * Rejects if the room has no link to send or the message doesn't go out.
 */
export const sendRoomLink = async ({
  conversationId,
  roomId,
  refreshToken,
}: RoomLink): Promise<void> => {
  // The room can drop out of the store while its card is still open
  if (!roomId || !refreshToken) {
    throw new Error('Video room has no link to send');
  }
  const talkjsSession = window.__RSOS?.talkjsSession;
  if (!talkjsSession) {
    throw new Error('TalkJS session is not ready');
  }

  const roomUrl = `${window.location.protocol}//${window.location.host}/live-video/rooms/${roomId}?refresh_token=${refreshToken}`;
  const chat = await talkjsSession.getOrCreateConversation(conversationId);
  await chat.sendMessage(`${LIVESTREAM_PROMPT} ${roomUrl}`);
};

/**
 * The chat a room link can go out over: the caller's open TF911 chat, else
 * their open TT911 or admin-line chat, else null. A caller rarely has both,
 * and either one reaches the same phone.
 */
export const useOpenConversationId = (
  callerId: string | null,
): string | null => {
  const tf911Conversation = useSelectedTextFrom911Conversation(callerId ?? '');
  const conversation = useGetConversation(callerId);

  const tf911ConversationId = tf911Conversation?.data?.conversation_id;
  if (
    tf911ConversationId &&
    tf911Conversation.data.custom?.STATUS !== CONVERSATION_ENDED
  ) {
    return tf911ConversationId;
  }

  if (
    getHasConversationBeenInitiated(conversation) &&
    !getIsConversationEnded(conversation)
  ) {
    return getConversationID(conversation);
  }

  return null;
};
